import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import { json, Outlet, useLoaderData } from "@remix-run/react";
import {
  ClientsSection,
  ClientsVerifySection,
  MarketplaceSection,
  SolutionsSection,
  StartSection,
  UpdateSection,
  VerifySection,
  WorkSection,
} from "@bohiio/ui-components";
import { BaseTemplate, EnvValues } from "../templates/base";
import { getWsLink, WhatsappMessages } from "../utils/whatsappText";
import { getMarketplaceLinks } from "../utils/links";
import { authenticate } from "../utils/authenticate";
import { withError } from "../utils/http";
import { getFullUserInfo } from "../utils/profile/profile.server";
import { UserInfo } from "../utils/profile/types";
import { accessCookie } from "../utils/cookies.server";

export function links() {
  return [
    // add a local stylesheet,
    // ...(cssBundleHref ? [{ rel: "stylesheet", href: cssBundleHref }] : []),
  ];
}

export const meta: MetaFunction = () => {
  return [
    { title: "bohiio" },
    {
      name: "description",
      content:
        "Making possible millions of real estate deals all over the world quickly and safely.",
    },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const CDN_URL = process.env.BOHIIO_CDN_URL;
  const SERVICE_PHONE_NUMBER = process.env.SERVICE_PHONE_NUMBER;
  const BOHIIO_MARKETPLACE_URL = process.env.BOHIIO_MARKETPLACE_URL;
  const INSTAGRAM_URL = process.env.INSTAGRAM_URL;
  const FACEBOOK_URL = process.env.FACEBOOK_URL;
  const X_SOCIAL_MEDIA_URL = process.env.X_SOCIAL_MEDIA_URL;
  const BOHIIO_SUPPORT_MAIL_ADDRESS = process.env.BOHIIO_SUPPORT_MAIL_ADDRESS;

  const envJSON = {
    env: {
      CDN_URL,
      SERVICE_PHONE_NUMBER,
      BOHIIO_MARKETPLACE_URL,
      INSTAGRAM_URL,
      FACEBOOK_URL,
      X_SOCIAL_MEDIA_URL,
      BOHIIO_SUPPORT_MAIL_ADDRESS,
    } as EnvValues,
    userInfo: null as unknown as UserInfo,
  };

  let [errorAuthentication, accessToken] = await withError(
    authenticate(request)
  );
  if (errorAuthentication || !accessToken) {
    return json(envJSON);
  }

  let [errorUser, userInfo] = await withError(getFullUserInfo(accessToken));
  if (errorUser) {
    return json(envJSON);
  }

  return json({ ...envJSON, userInfo: userInfo });
}

export default function Index() {
  const { env, userInfo } = useLoaderData<typeof loader>();
  const { BOHIIO_MARKETPLACE_URL, CDN_URL, SERVICE_PHONE_NUMBER } = env;
  const marketplaceLinks = getMarketplaceLinks(BOHIIO_MARKETPLACE_URL);

  return (
    <BaseTemplate
      env={env}
      userActionProps={{
        showAvatarUser: !!userInfo,
        showLoginButton: !userInfo,
        showNotifications: !!userInfo,
        showPublishProperties: !!userInfo,
      }}
      userProfile={userInfo}
      links={marketplaceLinks}
    >
      <div id="verify" className="app-verify-section app-section">
        <div className="app-verify-section--container app-section--container py-16 md:py-0">
          <div className="app-verify-section--container--article">
            <div
              style={{
                width: "100%",
                height: "100%",
              }}
            >
              <VerifySection
                imageSource={`${CDN_URL}/certified-property.png`}
                onClickStartVerificationButton={() =>
                  window.location.assign(marketplaceLinks.verificatioRequest)
                }
                onClickRequestAdviserButton={() =>
                  window.open(
                    getWsLink(
                      SERVICE_PHONE_NUMBER!,
                      WhatsappMessages.verifyPropertyHelp
                    ),
                    "_blank"
                  )
                }
              />
            </div>
          </div>
        </div>
        <div className="app-clients-container py-10 md:py-0">
          <ClientsVerifySection>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((name) => (
              <div
                key={name}
                className="client items-center flex justify-center"
              >
                <img
                  srcSet={`${CDN_URL}/partners/${name}/${name}.png, ${CDN_URL}/partners/${name}/${name}@2x.png 2x`}
                  src={`${CDN_URL}/partners/${name}/${name}.png`}
                  alt="partner"
                />
              </div>
            ))}
          </ClientsVerifySection>
        </div>
      </div>
      <div id="works" className="app-works-section app-section py-16 md:py-0">
        <div className="app-section--container">
          <WorkSection
            techButtonContent={
              <a
                href={getWsLink(
                  SERVICE_PHONE_NUMBER!,
                  WhatsappMessages.verificationCoupon
                )}
                rel="noreferrer"
                target="_blank"
              >
                Obtener cupón de descuento
              </a>
            }
            certifyParagraph={
              <>
                Bohiio brinda un servicio integral de Certificación Inmobiliaria
                que minimiza el riesgo en tus operaciones. Antes de comprar o
                vender una propiedad, es fundamental realizar estudios
                exhaustivos para prevenir sorpresas desagradables al cerrar el
                trato.{" "}
                <span className="underline underline-active">
                  Nuestro compromiso es proporcionar a nuestros clientes las
                  herramientas necesarias para evitar cualquier tipo de estafa
                  inmobiliaria.
                </span>
              </>
            }
            imageContent={
              <img
                src={`${CDN_URL}/report-model.jpg`}
                alt="Modelo de reporte"
              />
            }
          />
        </div>
      </div>
      <div
        id="clients"
        className="app-clients-section app-section !py-8 md:py-0"
      >
        <div className="app-clients-section--background"></div>
        <div className="app-section--container ">
          <ClientsSection
            onClickButton={() =>
              window.open(
                getWsLink(
                  SERVICE_PHONE_NUMBER!,
                  WhatsappMessages.refereeInfoProgram
                ),
                "_blank"
              )
            }
            title="¿Trabajas en bienes raíces?"
            content={
              <div className="app-section--container--content mt-4 mb-8">
                <p className="mb-4">
                  ¡Aprovecha tu experiencia para generar ganancias adicionales
                  todas las semanas con Bohiio!
                </p>
                <p className="mb-4">
                  ¡Amplía tus oportunidades en el sector inmobiliario!
                  Regístrate para ser parte de nuestro programa de referidos y
                  comienza a generar ingresos adicionales de manera constante.
                </p>
                <p className="mb-4">
                  Al referir clientes que necesiten realizar una tasación o
                  regularizar un inmueble,{" "}
                  <span className="underline underline-active">
                    obtendrás una comisión del 10%
                  </span>{" "}
                  sobre el total de la factura cobrada al cliente que refieras.
                </p>
              </div>
            }
            options={[
              {
                id: "quoter",
                buttonContent: "Empezar ahora",
                content: (
                  <div>
                    <p className="mb-4">
                      Únete a Bohiio y destaca tu experiencia en tasaciones.
                      Disfruta de la flexibilidad para organizar tu tiempo
                      mientras generas ingresos constantes cada semana, ayudando
                      a nuestros clientes a tomar decisiones clave.
                    </p>
                    <p>
                      Regístrate como colaborador y amplía tus oportunidades de
                      ingresos al trabajar con nosotros.
                    </p>
                  </div>
                ),
                title: "¿Eres tasador o valuador?",
                onClickButton() {
                  window.open(
                    getWsLink(
                      SERVICE_PHONE_NUMBER!,
                      WhatsappMessages.appraiserWork
                    ),
                    "_blank"
                  );
                },
              },
              {
                id: "layer",
                buttonContent: "Empezar ahora",
                content: (
                  <div>
                    <p className="mb-4">
                      Forma parte de nuestra red legal en Bohiio. Aporta tu
                      conocimiento en procesos legales y colabora en la
                      regularización de propiedades, ayudando a nuestros
                      clientes a lograr sus objetivos inmobiliarios.
                    </p>
                    <p>
                      Regístrate como colaborador y amplía tus oportunidades de
                      ingresos al trabajar con nosotros.
                    </p>
                  </div>
                ),
                title: "¿Abogado?",
                onClickButton() {
                  window.open(
                    getWsLink(
                      SERVICE_PHONE_NUMBER!,
                      WhatsappMessages.layerWork
                    ),
                    "_blank"
                  );
                },
              },
              {
                id: "land",
                buttonContent: "Empezar ahora",
                content: (
                  <div>
                    <p className="mb-4">
                      Con Bohiio, tu experiencia en mensuras catastrales puede
                      marcar la diferencia. Participa en proyectos técnicos
                      esenciales y contribuye al crecimiento del sector
                      inmobiliario mientras generas ingresos adicionales.
                    </p>
                    <p>
                      Regístrate como colaborador y amplía tus oportunidades de
                      ingresos al trabajar con nosotros.
                    </p>
                  </div>
                ),
                title: "¿Agrimensor?",
                onClickButton() {
                  window.open(
                    getWsLink(
                      SERVICE_PHONE_NUMBER!,
                      WhatsappMessages.surveyorWork
                    ),
                    "_blank"
                  );
                },
              },
            ]}
          />
        </div>
      </div>
      <div id="update" className="app-update-section app-section !py-8 md:py-0">
        <div className="app-section--container">
          <UpdateSection
            figureContent={
              <img
                src={`${CDN_URL}/title-ri.png`}
                alt="Certificado de titulo"
              />
            }
            onClickAdvisorButton={() =>
              window.open(
                getWsLink(SERVICE_PHONE_NUMBER!, WhatsappMessages.paymentsInfo),
                "_blank"
              )
            }
            onClickQuoteButton={() =>
              window.open(
                getWsLink(
                  SERVICE_PHONE_NUMBER!,
                  WhatsappMessages.certifyRealStateRequestQuote
                ),
                "_blank"
              )
            }
            content={
              <>
                Nos encargamos de regularizar tu inmueble hasta obtener el
                Certificado de Título, con opciones de pago flexibles de{" "}
                <span className="underline underline-active">
                  hasta 36 cuotas mensuales sin intereses ni penalidades.
                </span>{" "}
                Puedes pagar automáticamente con tarjeta y recibir asistencia
                durante todo el proceso de forma automatizada.
              </>
            }
          />
        </div>
      </div>
      <div
        id="solutions"
        className="app-solutions-section app-section !py-8 md:py-0"
      >
        <div className="app-section--container">
          <SolutionsSection
            figureContent={
              <img
                src={`${CDN_URL}/land-partners.jpg`}
                alt="Certificado de titulo"
              />
            }
            onClickPlansButton={() =>
              window.location.assign(marketplaceLinks.plans)
            }
            onClickAdvisorButton={() =>
              window.open(
                getWsLink(
                  SERVICE_PHONE_NUMBER!,
                  WhatsappMessages.verificationPlansHighVolume
                ),
                "_blank"
              )
            }
          />
        </div>
      </div>
      <div
        id="marketplace"
        className="app-marketplace-section app-section !py-8 md:py-0"
      >
        <div className="app-section--container">
          <MarketplaceSection
            onClickExploreButton={() => {
              console.log("clicked");
              window.location.assign(BOHIIO_MARKETPLACE_URL!);
            }}
            onClickTendencyButton={() =>
              window.location.assign(`${BOHIIO_MARKETPLACE_URL}/tendency`)
            }
            content={
              <>
                <p className="mb-8">
                  Además de ofrecer seguridad a nuestros clientes en el sector
                  inmobiliario gracias a nuestras verificaciones y todas las
                  facilidades para regularizar sus inmuebles, hemos creado un
                  marketplace inmobiliario donde puedes publicar todas tus
                  propiedades en venta o alquiler sin ningún costo. Este
                  servicio adicional maximiza tu alcance y potencia tu
                  visibilidad
                </p>
              </>
            }
            cards={[
              {
                className: "h-full",
                header: (
                  <figure className="bg-[#e7e6e6]">
                    <img
                      src={`${CDN_URL}/main-tendency.png`}
                      alt="Principales tendencias"
                    />
                  </figure>
                ),
                children: (
                  <div className="p-8">
                    <h3 className="mb-2 text-2xl">Principales tendencias</h3>
                    <p className="text-[#425466]">
                      Destaca tu marca personal con hashtags personalizados como{" "}
                      <strong>
                        <span style={{ color: "var(--primary-color)" }}>#</span>
                        JuanRealEstate
                      </strong>{" "}
                      y menciona a colegas o tu inmobiliaria para potenciar tu
                      visibilidad 🏠✨.
                    </p>
                  </div>
                ),
              },
              {
                className: "h-full",
                header: (
                  <figure className="bg-[#e7e6e6]">
                    <img
                      src={`${CDN_URL}/certified-property-card.png`}
                      alt="Propiedad certificada"
                    />
                  </figure>
                ),
                children: (
                  <div className="p-8">
                    <h3 className="mb-2 text-2xl">Propiedad certificada</h3>
                    <p className="text-[#425466]">
                      Las propiedades que superan nuestro estricto proceso de
                      verificación aparecen como certificadas en nuestro
                      marketplace, ofreciendo mayor seguridad a tus clientes
                      potenciales.
                    </p>
                  </div>
                ),
              },
              {
                className: "h-full",
                header: (
                  <figure className="bg-[#e7e6e6]">
                    <img
                      src={`${CDN_URL}/no-middleman.png`}
                      alt="Sin intermediarios"
                    />
                  </figure>
                ),
                children: (
                  <div className="p-8">
                    <h3 className="mb-2 text-2xl">Sin intermediarios</h3>
                    <p className="text-[#425466]">
                      En nuestro marketplace, facilitamos comunicaciones
                      directas con interesados a través de WhatsApp, sin
                      intermediarios.
                    </p>
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>
      <div id="start" className="app-start-section app-section !py-8 md:py-0">
        <div className="app-section--container">
          <StartSection
            mainButtonContent={"Comenzar verificación"}
            onClickMainButton={() =>
              window.location.assign(marketplaceLinks.verificatioRequest)
            }
            onClickGainsButton={() =>
              window.open(
                getWsLink(
                  SERVICE_PHONE_NUMBER!,
                  WhatsappMessages.refereeInfoProgram
                ),
                "_blank"
              )
            }
            onClickPlansButton={() =>
              window.open(
                getWsLink(
                  SERVICE_PHONE_NUMBER!,
                  WhatsappMessages.planServiceInformation
                ),
                "_blank"
              )
            }
          />
        </div>
      </div>
      <Outlet />
    </BaseTemplate>
  );
}
